import React, { useState } from 'react';
import ActionableItem from 'components/common/receipt/actionable_item';
import { Box, Divider, Grid } from '@mui/material';
import CartBanner, { CART_BANNER_CONFIGS } from 'components/common/cart_banner';
import { fetchSoftwareSummaries } from 'utilities/software';
import { onCartPageClick } from 'components/pages/cart/utilities';
import { useOrderDetails } from 'components/context/order_details';

export const SECTION_TYPES = Object.freeze({
  HARDWARE: 'hardware',
  SOFTWARE: 'software',
});

const ActionableCartSection = (props) => {
  const { trackingData } = useOrderDetails();
  const [disabled, setDisabled] = useState(false);

  const {
    orderData,
    sectionTitle,
    cartSection,
    shipQuotes,
    cartClient,
    orderRefresh,
    titleButton,
    emptyComponent,
    sectionType,
    bopisEligible,
  } = props;

  const handleQtyChange = (variantToken, oldQty, newQty) => {
    const delta = newQty - oldQty;
    if (delta === 0) {
      return;
    }
    setDisabled(true);
    const action = delta > 0 ? 'add' : 'subtract';
    cartClient
      .updateQuantity(variantToken, Math.abs(delta), [], action)
      .then(orderRefresh)
      .then(() => {
        return new Promise((resolve) => {
          const targetProduct = cartSection.find(
            (item) => item.product_variant.token === variantToken,
          );

          onCartPageClick({
            actionItem: 'change_quantity',
            additionalParameters: {
              targetProduct: {
                productKey: targetProduct.product_key,
                variantToken: targetProduct.product_variant.token,
                title: targetProduct.product_variant.title,
              },
              ...trackingData,
              action,
              delta,
            },
          });
          resolve();
        });
      })
      .finally(() => setDisabled(false));
  };

  const cartItems = cartSection.reduce((acc, cartItem, index) => {
    acc.push(
      <ActionableItem
        key={`item-${cartItem.id}`}
        category={cartItem.category}
        token={cartItem.product_variant.token}
        img={cartItem.img}
        slug={cartItem.product_variant.slug}
        hasPdp={cartItem.has_pdp}
        qty={cartItem.quantity}
        limit={cartItem.max_quantity}
        title={cartItem.product_variant.title}
        price={cartItem.price}
        currency={cartItem.currency}
        outOfStock={cartItem.out_of_stock}
        disclaimer={cartItem.disclaimer}
        shipQuote={shipQuotes?.find((quote) => quote.id === cartItem.id)}
        parts={cartItem.parts}
        plan={cartItem.plan}
        softwareGroup={cartItem.software_group}
        onQtyChange={handleQtyChange}
        disabled={disabled}
        isSoftware={cartItem.is_software}
        baseProductKey={cartItem.base_product_key}
        bopisEligible={bopisEligible}
      />,
    );

    if (index !== cartSection.length - 1) {
      acc.push(<Divider key={`hr-${cartItem.id}`} sx={{ my: 1 }} />);
    }

    return acc;
  }, []);

  const softwareBanners = fetchSoftwareSummaries(orderData).map((summary) => {
    if (!summary) return null;
    const { softwareName, subscriptionState } = summary;
    const config = CART_BANNER_CONFIGS[subscriptionState];

    return (
      <CartBanner
        key={softwareName}
        config={config}
        softwareName={softwareName}
      />
    );
  });

  if (cartSection.length === 0 && !emptyComponent) return null;

  return (
    <div>
      <Grid container justifyContent="space-between" alignItems="flex-end">
        <h3>{sectionTitle}</h3>
        {titleButton && titleButton}
      </Grid>
      {sectionType === SECTION_TYPES.SOFTWARE && softwareBanners}
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1.5,
          boxSizing: 'border-box',
          marginTop: 2,
        }}
      >
        {cartSection.length === 0 && emptyComponent
          ? emptyComponent
          : cartItems}
      </Box>
    </div>
  );
};

export default ActionableCartSection;
